import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import { UUID } from '@/types/types';
import { Product } from '@/api/ProductApi';
import { ProductType } from '@/api/ProductTypeApi';
import { getFileSrc } from '@/utils/utilities';
import { ProductTypeVariation } from '@/api/ProductTypeVariationApi';
import { pick } from 'lodash';
import APP_CONFIG from '@/configs/appConfig';

export interface CartProduct {
	id: UUID;
	productTypeId: UUID;
	imagePath?: string;
	productTypeVariation: Pick<
		ProductTypeVariation,
		'id' | 'name' | 'translations'
	>;
}

interface CartItem {
	id: UUID;
	selected: boolean;
	quantity: number;
	cartProduct: CartProduct;
}

export interface CartState {
	items: CartItem[];
}

const initialState: CartState = {
	items: [],
};

function syncStorage(state: CartState, updater: (state: CartState) => void) {
	updater(state);
	localStorage.setItem(APP_CONFIG.CART.KEY, JSON.stringify(current(state)));
}

export const cartSlice = createSlice({
	name: StoreKey.CART,
	initialState,
	reducers: {
		addCartItem: (
			state,
			{
				payload,
			}: PayloadAction<{
				product: Product;
				productType: ProductType;
				productTypeVariation: ProductTypeVariation;
				quantity: number;
			}>
		) => {
			syncStorage(state, () => {
				state.items.push({
					id: crypto.randomUUID(),
					selected: true,
					quantity: payload.quantity,
					cartProduct: {
						id: payload.product.id,
						productTypeId: payload.productType.id,
						productTypeVariation: pick(payload.productTypeVariation, [
							'id',
							'name',
							'translations',
						]),
						imagePath: getFileSrc(payload.product.image?.path),
					},
				});
			});
		},
		removeCartItem: (state, action: PayloadAction<UUID>) => {
			syncStorage(state, () => {
				state.items = state.items.filter((item) => item.id !== action.payload);
			});
		},
		updateItem: (
			state,
			action: PayloadAction<{ id: UUID; payload: Partial<CartItem> }>
		) => {
			syncStorage(state, () => {
				const item = state.items.find((item) => item.id === action.payload.id);
				if (item) Object.assign(item, action.payload.payload);
			});
		},
		clearCart: (state) => {
			syncStorage(state, () => {
				state.items = state.items.filter((item) => !item.selected);
			});
		},
	},
});

export const cartActions = cartSlice.actions;

export const selectCartState = (state: RootState) => state.cart;
export default cartSlice.reducer;
